import type { ReactNode } from 'react'
import type { PromoCode } from '../promo/codes'
import { openPromoModal } from '../promo/modal'
import { usePromo } from '../promo/store'
import { track } from '../runtime/analytics'
import { ArrowIcon, CheckIcon } from './ui/PromoIcons'

/**
 * A promo for the pricing page, a pill above the plans. PromoBanner's
 * sibling, in the shape of a call to action rather than a bar: navy with a
 * drifting purple glow while the visitor's promo is on offer, where a click
 * opens the sign-up form that earns it, and green and blue with a check once
 * it is active, with nothing left to tap.
 *
 * It reads the same promo store as the bar, so the two change at the same
 * moment, and opens the same modal the same way. A component of its own rather
 * than a variant of the bar: the two share no markup, and each has its own words.
 */

export interface PromoPillProps {
  /**
   * The Webflow modal the pill opens (`data-modal`), as a CSS selector: the
   * site's `.contact-modal5_component` without it, one for every visitor, or
   * one per code as JSON, as the bar takes it.
   */
  modal?: unknown
}

interface Wording {
  /** The navy pill's call to sign up, after "Tap" or "Click". */
  offer: ReactNode
  /** The green pill. */
  applied: ReactNode
}

const WORDING: Record<PromoCode, Wording> = {
  WELCOME40: {
    offer: 'to get 40% off your first kit',
    applied: (
      <>
        You’re getting <strong className="font-bold">40% off your first kit</strong>
      </>
    ),
  },
  FIRSTFREE: {
    offer: 'to get Your First Kit FREE',
    applied: (
      <>
        You’re getting <strong className="font-bold">Your First Kit FREE</strong>
      </>
    ),
  },
}

const PILL =
  'mx-auto flex w-full max-w-md items-center gap-3 rounded-full border-2 px-5 py-3 text-[17px] leading-snug font-medium'

export function PromoPill({ modal }: PromoPillProps) {
  const code = usePromo((state) => state.code)
  const active = usePromo((state) => state.active)
  const wording = WORDING[code]

  function open() {
    track({ kind: 'select_promotion', code, component: 'PromoPill' })
    openPromoModal(modal, code, 'PromoPill')
  }

  return (
    // Polite, so the pill turning green after the sign-up is announced.
    <div aria-live="polite" data-testid="promo-pill">
      {active ? (
        <p
          className={`${PILL} border-transparent bg-promo-applied text-left text-promo-ink`}
          data-testid="promo-applied"
        >
          <CheckIcon size={28} />
          <span className="flex-1">{wording.applied}</span>
        </p>
      ) : (
        <button
          type="button"
          onClick={open}
          className={`${PILL} border-transparent bg-promo-offer pl-7 text-center text-white shadow-[0_4px_0_0_rgb(23_19_63/0.35)] outline-none transition hover:brightness-110 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-promo-ink motion-safe:animate-promo-drift`}
          data-testid="promo-offer"
        >
          {/* A phone is tapped and a mouse clicks, whatever the screen's width. */}
          <span className="flex-1">
            <span className="pointer-coarse:hidden">Click</span>
            <span className="hidden pointer-coarse:inline">Tap</span> {wording.offer}
          </span>
          <ArrowIcon size={28} />
        </button>
      )}
    </div>
  )
}
